import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useLocation, useMatch } from 'react-router-dom';

import { Footer } from '@workspace/shared/components/Footer';
import { Typography } from '@workspace/ui/components/Typography';

import { company } from '~/company/Company';
import { FooterCDNavLinks } from '~/components/Footer/FooterCD/FooterCDNavLinks';
import FooterDownloadAppButtons from '~/components/Footer/FooterDownloadAppButtons';
import FooterLanguagePicker from '~/components/Footer/FooterLanguagePicker';
import { FooterSocialLinks } from '~/components/Footer/FooterSocialLinks';
import { FooterSupportButton } from '~/components/Footer/FooterSupportButton';
import { FooterMini } from '~/components/FooterMini';
import { HeaderV3 } from '~/components/HeaderV3';
import PageHeader from '~/components/isolated/PageHeader';
import { SupportFloatingButton } from '~/components/SupportFloatingButton';
import { useCheckoutBack } from '~/hooks/useCheckoutBack';
import { useFooterHeight } from '~/hooks/useFooterHeight';
import { useHeaderHeight } from '~/hooks/useHeaderHeight';
import { useStores } from '~/hooks/useStore';
import { orderStore } from '~/stores';
import { normalizeOrderId } from '~/utils/normalizeOrderId';

import FooterSaas from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import HeaderMini from '../../components/HeaderMini/HeaderMini';
import { OrderDateText } from '../Order/components/OrderDateText';
import OrderStatusBadgeV3 from '../OrderV3/components/OrderStatusBadgeV3';
import ReturnStatusBadgeV3 from '../OrderV3/components/ReturnStatusBadgeV3';
import { CONTENT_ID, ROUTES } from './constants';
import styles from './DesktopLayout.module.scss';
import { getPageHeaderConfig } from './pageHeaderConfig';

interface DesktopLayoutProps {
  children: React.ReactElement;
  layoutRef: React.Ref<HTMLDivElement>;
}

const DesktopLayout = ({ children, layoutRef }: DesktopLayoutProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { heroBanner } = useStores();
  const matchOrderPayment = useMatch(ROUTES.order_payment);
  const onCheckoutBack = useCheckoutBack();

  const headerRef = useRef<HTMLHeadingElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const isOrderPage = pathname.startsWith('/order/');

  const header = (() => {
    // Order page in V3: render PageHeader with a dynamic title built from
    // the order currently being viewed (set by OrderV3 into orderStore.viewedOrder).
    // We can't put this in pageHeaderConfig because the title depends on
    // async-loaded order data. Only mcosmetic renders OrderV3 (see Root.tsx),
    // so other companies must keep the regular pageHeaderConfig header.
    if (company.match({ mcosmetic: true, default: false }) && isOrderPage) {
      const viewedOrder = orderStore.viewedOrder;
      const orderTitle = viewedOrder ? (
        <span className="flex items-center gap-3">
          <Typography.Header100Medium as="span" className="text-text-primary">
            {t('phrases:orderNumber', {
              number: normalizeOrderId(viewedOrder.order_id),
            })}
          </Typography.Header100Medium>
          <Typography.Header100Medium as="span" className="text-text-secondary">
            <OrderDateText createdAt={viewedOrder.created_at} />
          </Typography.Header100Medium>
        </span>
      ) : (
        ''
      );

      const orderBadges = viewedOrder ? (
        <span className="flex items-center gap-2">
          <OrderStatusBadgeV3 status={viewedOrder.public_status} />
          <ReturnStatusBadgeV3 summary={viewedOrder.returns_summary} />
        </span>
      ) : null;

      return (
        <PageHeader
          ref={headerRef}
          title={orderTitle}
          rightContent={orderBadges}
          showBackButton
          backButtonText={t('continueShopping')}
          backButtonPath="/"
        />
      );
    }

    // Cart, Checkout and Cabinet pages use PageHeader
    const pageHeaderConfig = getPageHeaderConfig(pathname, false, t);
    if (company.newDesignSystemEnabled && pageHeaderConfig) {
      // Skip PageHeader on desktop if hideOnDesktop flag is set (use default header instead)
      if (!pageHeaderConfig.hideOnDesktop) {
        return (
          <PageHeader
            {...pageHeaderConfig}
            ref={headerRef}
            onBackClick={
              pathname === ROUTES.checkout ? onCheckoutBack : undefined
            }
          />
        );
      }
    }

    if (!company.hasNewCheckoutElemtnts) {
      if (matchOrderPayment) {
        return <HeaderMini ref={headerRef} />;
      }
      return <Header ref={headerRef} />;
    }

    switch (pathname) {
      case ROUTES.checkout:
        return company.newDesignSystemEnabled ? <HeaderV3 /> : <HeaderMini />;
      default:
        return <Header ref={headerRef} />;
    }
  })();

  const isCompactFooter = company.match({ localee: true, default: false });

  const Variant1 = (
    <Footer.FooterBaseSaas
      logoWidth={company.match({
        ocado: 'w-32',
        default: 'w-78',
      })}
      logo={{ desktop: company.images.logo.vector }}
      name={company.config.name}
      compact={isCompactFooter}
    >
      <Footer.TopSection>
        <FooterCDNavLinks variant="light" className="flex-1" />
        {company.isFooterAppDownloadButtonsEnabled ? (
          <FooterDownloadAppButtons />
        ) : (
          company.config.referral.downloadAppLink && (
            <div
              className={classes([
                'flex flex-col items-center gap-3',
                isCompactFooter ? 'w-[92px]' : 'w-[125px]',
              ])}
            >
              <div
                className={classes([
                  'bg-graphic-regular-white',
                  isCompactFooter
                    ? 'h-[92px] rounded-4 p-2'
                    : 'h-[125px] rounded-6 p-[10px]',
                ])}
              >
                <QRCode
                  value={company.config.referral.downloadAppLink}
                  size={isCompactFooter ? 76 : 105}
                  offset={isCompactFooter ? 8 : 10}
                />
              </div>
              <Typography.Text200Medium className="text-center">
                {t('downloadTheApp')}
              </Typography.Text200Medium>
            </div>
          )
        )}
      </Footer.TopSection>
      <Footer.Socials>
        <div
          className={classes([
            'grid',
            isCompactFooter ? 'grid-cols-[1fr_92px]' : 'grid-cols-[1fr_125px]',
          ])}
        >
          <FooterSocialLinks variant="light-outline" iconSize={20}>
            <FooterSupportButton className="-order-1 mr-2 inline-flex h-10 cursor-pointer items-center gap-[6px] rounded-full border-none bg-navigation-action-brand px-4 py-3 text-navigation-text-in-button transition-colors duration-300 [font:var(--text-200-semibold)] hover:bg-navigation-action-brand-hover" />
          </FooterSocialLinks>
        </div>
      </Footer.Socials>
      <Footer.LanguagePicker>
        <FooterLanguagePicker />
      </Footer.LanguagePicker>
    </Footer.FooterBaseSaas>
  );

  const footer = (() => {
    const footerComp = company.match({
      mcosmetic: Variant1,
      ocado: Variant1,
      heimkaup: Variant1,
      localee: Variant1,
      circlek: Variant1,
      default: <FooterSaas />,
    });

    if (!company.hasNewCheckoutElemtnts) {
      if (matchOrderPayment) {
        return <FooterMini ref={footerRef} />;
      }
      return footerComp;
    }

    switch (pathname) {
      case ROUTES.checkout:
        return <FooterMini />;
      default:
        return footerComp;
    }
  })();

  useHeaderHeight(headerRef, [pathname]);
  useFooterHeight(footerRef, [pathname]);

  return (
    <div
      className={classes([
        'page-layout',
        styles.desktopLayout,
        company.newDesignSystemEnabled && styles.redesign,
      ])}
      ref={layoutRef}
    >
      {header}

      {company.newDesignSystemEnabled ? (
        <div
          className={classes([
            styles.content,
            heroBanner.shouldShowSection &&
              pathname === ROUTES.index &&
              styles.withHeroBanner,
          ])}
          id={CONTENT_ID}
        >
          {/* I DONT KNOW WHY, BUT IT EMPTY DIV FIXED HEADER CORNERS FOR WINDOW CHROME */}
          <div className={styles.mock} />
          {children}
          {footer}
        </div>
      ) : (
        <>
          {children}
          {footer}
        </>
      )}

      <SupportFloatingButton />
    </div>
  );
};

export default observer(DesktopLayout);
